import React from 'react';
import SubPage from '../components/site/SubPage';
import {
  ClosingBrand,
  PageSection,
  PageTitleBox,
  SectionHeading,
} from '../components/site/SubPageParts';

const IMG = '/images/site/senior-challenge';

// Each block pairs a photograph with its copy; the mockup keeps the image on
// the left throughout, heading above.
const SECTIONS = [
  {
    heading: 'Digital Literacy.',
    image: `${IMG}/digital-literacy.jpeg`,
    alt: 'An older woman at a laptop with a younger relative helping',
    body: [
      'Pew Research found 67% of seniors, 65 plus, are digitally connected. This makes seniors a prime group for scammers due to their limited familiarity with internet schemes, frauds and financial crimes.',
      'Broadly speaking, seniors lack the ScammerAwareness to safely navigate the digital threat landscape. Because of this they are easy targets, tricked into sharing sensitive, personal or financial data with Cyberfraudsters.',
      'As we age our mental skills to identify and quickly process threats decline, making it even harder for seniors to avoid being victimized by a scammer. This combination creates a perfect storm for emotional and financial exploitation.',
    ],
  },
  {
    heading: 'Raised in a different world.',
    image: `${IMG}/different-world.jpeg`,
    alt: 'An older man on the phone with a laptop on his knees',
    body: [
      'Scammers are beasts of prey, and they understand and use dark psychology techniques to abuse their targets. They are master manipulators that hunt, lure and coach their victims to act against their own best interest. Seniors by contrast are honest, decent, good-natured people — and that is what makes them a perfect target.',
      'These money-driven digital violators zero in and exploit seniors’ trusting nature. Seniors were raised in a different generation; times were different, with different values, and one’s word was one’s bond. While these are outstanding qualities, it also makes them more vulnerable to scammers.',
    ],
  },
  {
    heading: 'Scammer 401K.',
    image: `${IMG}/nest-egg.jpeg`,
    alt: 'A bird’s nest holding a golden egg and a rolled hundred-dollar bill',
    body: [
      'John Dillinger, the famous bank robber of the 1930s, was asked why he robbed banks. He responded: because that is where the money is.',
      'Scammers target seniors for the same reason. They have spent a lifetime working and saving for retirement, own their home, and hold substantial savings, high credit-card limits and other investments, making them an attractive mark. Scammers know that if they gain access to these accounts, they can quickly rack up large debts before anyone notices.',
    ],
  },
  {
    heading: 'Personal Independence.',
    image: `${IMG}/personal-independence.jpeg`,
    alt: 'Weathered hands tying a shoelace',
    body: [
      'As we get older, we increasingly lose our independence. We resist — we fight, we hold on. This fear makes the senior community more susceptible to scammer tactics. Coupling the loss of personal independence with embarrassment and questions of judgment creates an unholy alliance that works in the scammer’s favor.',
      'It incentivizes seniors to keep things hidden — out of sight, out of mind. The fear of losing freedom works to the advantage of the scammer, who uses it to exploit and maintain control over their target.',
    ],
  },
];

const SeniorChallenge = () => (
  <SubPage title="Senior Challenge">
    <PageTitleBox>Sitting in the Crosshairs.</PageTitleBox>

    {/* ---------------- Hero ---------------- */}
    <PageSection className="pt-4">
      <div className="relative">
        <img
          src={`${IMG}/crosshairs-couple.jpeg`}
          alt="An older couple smiling at the camera, in black and white"
          className="h-[240px] w-full object-cover small:aspect-[14/5] small:h-auto"
        />
        <img
          src={`${IMG}/crosshair.svg`}
          alt=""
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 h-[150px] w-[150px] -translate-x-1/2 -translate-y-1/2 object-contain lg:h-[300px] lg:w-[300px]"
        />
      </div>

      <p className="mt-6 text-[17px] leading-relaxed text-[#8A8A8A] md:text-[22px]">
        The internet enables cybercriminals to commit a wide variety of scams and frauds on an
        industrial scale.{' '}
        <strong className="font-bold text-black">
          There&rsquo;s no credible evidence that the current tidal wave will slow down, and by
          extension the misery it brings.
        </strong>{' '}
        The current crop of technology solutions lack the imagination or capability to keep up
        with the creativity of today&rsquo;s cybercriminals.
      </p>
    </PageSection>

    {/* ---------------- Body sections ---------------- */}
    {SECTIONS.map((section) => (
      <PageSection key={section.heading} className="pt-10">
        <SectionHeading className="mb-5">{section.heading}</SectionHeading>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-14">
          <img
            src={section.image}
            alt={section.alt}
            className="h-[240px] w-full object-cover small:aspect-[322/165] small:h-auto"
          />
          <div className="space-y-4 text-[15px] leading-relaxed text-black md:text-[17px]">
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      </PageSection>
    ))}

    {/* ---------------- Lack of awareness ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>Lack of ScammerAwareness.</SectionHeading>
      <img
        src={`${IMG}/lack-of-awareness.jpeg`}
        alt="A hooded figure reaching towards a password field amid streams of code"
        className="mt-5 h-[240px] w-full object-cover small:aspect-[336/115] small:h-auto"
      />
      <div className="mt-6 space-y-5 text-[15px] leading-relaxed text-black md:text-[18px]">
        <p>
          Simply put &mdash; seniors fall for scams due to a lack of awareness. They lack the
          knowledge to identify and avoid scams, making them prime targets for emotional and
          financial exploitation. They&rsquo;re not familiar with the latest methods, tactics and
          approaches scammers use.
        </p>
        <p>
          Studies by the National Institute on Aging (NIA) show older adults unable to recognize
          the signs of fraudulent schemes. Older adults may be even more vulnerable to fraud and
          scams than previously thought, according to an NIA-funded study that mimicked a
          real-world government imposter scam, suggesting a sizable minority of older adults,
          including those without cognitive impairment, are vulnerable to fraud and scams.
        </p>
      </div>
    </PageSection>

    {/* ---------------- Closing ---------------- */}
    <PageSection className="pt-12">
      <img
        src={`${IMG}/tools-that-work.jpeg`}
        alt="An older couple looking at a laptop together"
        className="h-[280px] w-full object-cover small:aspect-[12/5] small:h-auto"
      />
    </PageSection>

    <ClosingBrand tagline="Tools that work for you." />
  </SubPage>
);

export default SeniorChallenge;
